/**
 * Alert - Notification Message Component
 *
 * Displays informational messages with different severity levels.
 * Renders as Box with icon and optional dismiss button.
 *
 * @example
 * <Alert variant="success">Profile updated successfully!</Alert>
 * <Alert variant="error" onClose={() => {}}>An error occurred</Alert>
 * <Alert variant="info">New features available</Alert>
 */
import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, CheckCircle, Info, XCircle, X } from "lucide-react";
import { Box, Text } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

const alertVariants = cva("flex items-start gap-3 p-4 rounded-lg", {
  variants: {
    variant: {
      info: "bg-blue-50 text-blue-900",
      success: "bg-green-50 text-green-900",
      warning: "bg-yellow-50 text-yellow-900",
      error: "bg-red-50 text-red-900",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

export interface AlertProps
  extends BaseComponentProps, VariantProps<typeof alertVariants> {
  /** Optional dismiss handler */
  onClose?: () => void;
  /** Hide default icon */
  hideIcon?: boolean;
}

/**
 * Alert component
 * Renders as a Box with contextual styling
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    { variant = "info", onClose, hideIcon, className, children, ...props },
    ref,
  ) => {
    const icons = {
      info: Info,
      success: CheckCircle,
      warning: AlertCircle,
      error: XCircle,
    };

    const iconColors = {
      info: "text-blue-600",
      success: "text-green-600",
      warning: "text-yellow-600",
      error: "text-red-600",
    };

    const effectiveVariant = variant || "info";
    const Icon = icons[effectiveVariant];

    return (
      <Box
        ref={ref}
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {!hideIcon && (
          <Icon
            className={cn(
              "w-5 h-5 flex-shrink-0 mt-0.5",
              iconColors[effectiveVariant],
            )}
          />
        )}
        <Box className="flex-1 min-w-0">
          <Text variant="body" className="text-sm">
            {children}
          </Text>
        </Box>
        {onClose && (
          <Box
            as="button"
            type="button"
            onClick={onClose}
            className={cn(
              "flex-shrink-0 p-0.5 rounded transition-colors",
              "hover:bg-black/5 active:scale-95",
            )}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </Box>
        )}
      </Box>
    );
  },
);

Alert.displayName = "Alert";
